import { apiOrigin } from '../utils/surface';

export type SettingValue = boolean | string | number | string[] | null;

export interface SettingsIssue {
  field: string;
  code: string;
  severity: 'error' | 'warning';
  remediation: string;
}

export interface EffectiveIntercomSettings {
  intercom_id?: string;
  property_id?: string;
  settings_version?: number;
  values: Record<string, SettingValue>;
  sources?: Record<string, string>;
  errors?: SettingsIssue[];
  warnings?: SettingsIssue[];
  summary?: string[];
}

export interface IntercomSettingsDocument {
  intercom_id: string;
  property_id: string;
  property_intercom_count: number;
  settings_version: number;
  values: Record<string, SettingValue>;
  editable?: Record<string, SettingValue>;
  effective: EffectiveIntercomSettings;
}

export interface IntercomSettingsPreview {
  preview_token: string;
  expires_at: number;
  changes: Record<string, SettingValue>;
  targets: Array<{
    intercom_id: string;
    name: string;
    result: 'update' | 'unchanged' | 'skip' | 'error';
    errors?: SettingsIssue[];
    issues?: SettingsIssue[];
  }>;
}

export interface IntercomUnlockDevice {
  id: string;
  name: string;
}

export interface SettingsPreviewRequest {
  expected_version: number;
  changes: Record<string, SettingValue>;
  scope: 'single' | 'all_property_intercoms';
  optional_unsupported_policy?: 'fail' | 'skip_reviewed';
}

export interface SettingsMutationRequest {
  expected_version: number;
  idempotency_key: string;
  changes: Record<string, SettingValue>;
  preview_token: string;
}

export interface BulkSettingsMutationRequest {
  idempotency_key: string;
  preview_token: string;
}

const request = async <T>(path: string, method: string, body?: unknown): Promise<T> => {
  const token = localStorage.getItem('intrlock_token') || '';
  const result = await fetch(`${apiOrigin()}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  const payload = await result.json().catch(() => ({}));
  if (!result.ok) {
    const error = new Error(payload?.error || `Intercom settings request failed (${result.status})`) as Error & { status?: number; code?: string };
    error.status = result.status;
    error.code = payload?.code;
    throw error;
  }
  return payload as T;
};

export async function getIntercomSettings(intercomId: string): Promise<IntercomSettingsDocument> {
  const document = await request<IntercomSettingsDocument>(`/intercom-configs/${encodeURIComponent(intercomId)}/settings`, 'GET');
  return { ...document, values: document.values ?? document.editable ?? document.effective?.values ?? {} };
}

export async function getIntercomUnlockDevices(propertyId: string): Promise<IntercomUnlockDevice[]> {
  const payload = await request<any>(`/devices?property_id=${encodeURIComponent(propertyId)}`, 'GET');
  const rows = Array.isArray(payload) ? payload : (payload?.items ?? payload?.data ?? []);
  return rows
    .filter((device: any) => {
      const type = String(device.device_type ?? device.type ?? '').toLowerCase();
      return ['lock', 'door', 'relay', 'gate'].includes(type) || Boolean(device.acs_provider || device.pdk_device_id);
    })
    .map((device: any) => ({
      id: String(device.id ?? device.uuid ?? ''),
      name: String(device.name ?? device.device_name ?? 'Door'),
    }))
    .filter((device: IntercomUnlockDevice) => Boolean(device.id));
}

export function previewIntercomSettings(intercomId: string, payload: SettingsPreviewRequest): Promise<IntercomSettingsPreview> {
  return request(`/intercom-configs/${encodeURIComponent(intercomId)}/settings/preview`, 'POST', payload);
}

export function saveIntercomSettings(intercomId: string, payload: SettingsMutationRequest): Promise<any> {
  return request(`/intercom-configs/${encodeURIComponent(intercomId)}/settings`, 'PATCH', payload);
}

export function bulkApplyIntercomSettings(propertyId: string, payload: BulkSettingsMutationRequest): Promise<any> {
  return request(`/properties/${encodeURIComponent(propertyId)}/intercom-settings/bulk`, 'POST', payload);
}
